import React from 'react';
import { i18n } from 'config/locales';
import { Summary, NameText, DateSummary } from 'components/Summary';
import { FOREIGN, FOREIGN_BUSINESS_CONTACT } from 'config/formSections/foreign';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import { EmptyListInstance } from 'forms/shared/models';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { foreignBusinessAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import { isPVQForm } from 'helpers/form';
import ContactItem from './ContactItem';

const sectionConfig = {
  key: FOREIGN_BUSINESS_CONTACT.key,
  section: FOREIGN.name,
  store: FOREIGN.store,
  subsection: FOREIGN_BUSINESS_CONTACT.name,
  storeKey: FOREIGN_BUSINESS_CONTACT.storeKey,
};

export class Contact extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      List: this.props.List,
      HasForeignContact: this.props.HasForeignContact,
      ...queue,
    });
  };

  updateHasForeignContact = (values) => {
    this.update({
      HasForeignContact: values,
      List: values.value === 'Yes' ? this.props.List : { items: [], branch: {} },
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  summary = (item, index) => {
    const obj = (item && item.Item) || {};
    const date = DateSummary(obj.Date);
    const name = NameText(obj.Name);
    const govt = ((obj.Governments || {}).value || []);
    const govtListAsString = Array.isArray(govt) ? govt.join(', ') : '';

    let display = '';
    if (name && govtListAsString) {
      display = <span className="title-case">{`${name} (${govtListAsString})`}</span>;
    } else if (name) {
      display = <span className="title-case">{name}</span>;
    } else if (govtListAsString) {
      display = <span className="title-case">{govtListAsString}</span>;
    }

    return Summary({
      type: i18n.t('foreign.business.contact.collection.summary.item'),
      index,
      left: display,
      right: date,
      placeholder: i18n.t('foreign.business.contact.collection.summary.unknown'),
    });
  };

  render() {
    const isPVQ = isPVQForm(this.props.formType);
    const { errors, peopleWhoKnowYou, List } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const attribute = `List.items[${this.props.$index}]`;
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, foreignBusinessAddressNameFields);
    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return isPVQ ? (
      <div
        className="section-content foreign-business-contact"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_BUSINESS_CONTACT.key}
      >
        <h1 className="section-header">{i18n.t('foreign.pvq.subsection.business.contact')}</h1>
        <Branch
          name="has_foreign_contact"
          label={i18n.t('foreign.pvq.business.contact.heading.title')}
          attribute="HasForeignContact"
          labelSize="h4"
          {...this.props.HasForeignContact}
          warning
          onUpdate={this.updateHasForeignContact}
          required={this.props.required}
          onError={this.handleError}
          scrollIntoView={this.props.scrollIntoView}
          showDNK
        />

        {this.props.HasForeignContact.value === 'Yes'
          && (
            <Accordion
              {...this.props.List}
              defaultState={this.props.defaultState}
              scrollToBottom={this.props.scrollToBottom}
              onUpdate={this.updateList}
              onError={this.handleError}
              errors={accordionErrors}
              summary={this.summary}
              description={i18n.t('foreign.pvq.business.contact.collection.summary.title')}
              appendTitle={i18n.t('foreign.pvq.business.contact.collection.appendTitle')}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              additionalErrors={
                (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
              }
            >
              <ContactItem
                name="Item"
                attribute={`${attribute}.Item`}
                bind
                scrollIntoView={this.props.scrollIntoView}
                required={this.props.required}
                formType={this.props.formType}
              />
            </Accordion>
          )}
      </div>
    ) : (
      <div
        className="section-content foreign-business-contact"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_BUSINESS_CONTACT.key}
      >
        <h1 className="section-header">{i18n.t('foreign.subsection.business.contact')}</h1>
        {i18n.m('foreign.business.contact.para.intro')}
        <Branch
          name="has_foreign_contact"
          label={i18n.t('foreign.business.contact.heading.title')}
          attribute="HasForeignContact"
          labelSize="h4"
          {...this.props.HasForeignContact}
          warning
          onUpdate={this.updateHasForeignContact}
          required={this.props.required}
          onError={this.handleError}
          scrollIntoView={this.props.scrollIntoView}
        >
          {i18n.t('foreign.business.contact.para.branch')}
        </Branch>

        <Show when={this.props.HasForeignContact.value === 'Yes'}>
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            onUpdate={this.updateList}
            onError={this.handleError}
            errors={accordionErrors}
            summary={this.summary}
            description={i18n.t('foreign.business.contact.collection.summary.title')}
            appendTitle={i18n.t('foreign.business.contact.collection.appendTitle')}
            appendMessage={i18n.m('foreign.business.contact.collection.appendMessage')}
            appendLabel={i18n.t('foreign.business.contact.collection.append')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            additionalErrors={
              (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <ContactItem
              name="Item"
              attribute={`${attribute}.Item`}
              bind
              scrollIntoView={this.props.scrollIntoView}
              required={this.props.required}
            />
          </Accordion>
        </Show>
      </div>
    );
  }
}

Contact.defaultProps = {
  name: 'Contact',
  HasForeignContact: {},
  List: EmptyListInstance,
  onUpdate: () => { },
  onError: (value, arr) => arr,
  section: 'foreign',
  subsection: 'business/contact',
  addressBooks: {},
  dispatch: () => { },
  defaultState: true,
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(Contact, sectionConfig);
